import { toast } from 'sonner';

/**
 * Extracts a human-readable message from an RTK Query / fetch error.
 * The order service uses `{ errors: [{ errorMessage }] }` for validation
 * failures (matches portal's `error?.response?.data?.errors[0]?.errorMessage`).
 * Other endpoints surface a plain `message` string. We fall back to the HTTP
 * status when neither is present so the user gets something better than
 * "Something went wrong".
 */
export function extractMutationErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!error || typeof error !== 'object') return fallback;
  const e = error as {
    status?: number | string;
    error?: string;
    data?:
      | {
          errors?: Array<{ errorMessage?: string; message?: string }>;
          Errors?: Array<{ ErrorMessage?: string; Message?: string }>;
          message?: string;
          Message?: string;
          error?: string;
        }
      | string;
    message?: string;
  };
  const data = e.data as
    | (Exclude<typeof e.data, string> & {
        Messages?: string[];
        messages?: string[];
      })
    | string;
  if (data && typeof data === 'object') {
    // routing-service ErrorResult shape: { Messages: string[], Code, ErrorId }
    const routingMessage = (data.Messages ?? data.messages)?.find(
      (m): m is string => typeof m === 'string' && m.length > 0,
    );
    if (routingMessage) return routingMessage;
    // lowercase shape (order service, most REST endpoints)
    const first = data.errors?.[0];
    if (first?.errorMessage) return first.errorMessage;
    if (first?.message) return first.message;
    // capitalized shape (integration / transformation service ValidationErrorResponse)
    const firstCap = data.Errors?.[0];
    if (firstCap?.ErrorMessage) return firstCap.ErrorMessage;
    if (firstCap?.Message) return firstCap.Message;
    if (typeof data.message === 'string' && data.message.length > 0) return data.message;
    if (typeof data.Message === 'string' && data.Message.length > 0) return data.Message;
    if (typeof data.error === 'string') return data.error;
  }
  if (typeof data === 'string' && data.length > 0) return data;
  if (typeof e.message === 'string' && e.message.length > 0) return e.message;
  if (typeof e.error === 'string' && e.error.length > 0) return e.error;
  if (e.status) return `Request failed (status ${e.status}).`;
  return fallback;
}

/**
 * Shorthand for the common pattern: `await mutation(args).unwrap().catch(reportMutationError)`.
 * Returns void so it can plug directly into `.catch(reportMutationError)`.
 */
export function reportMutationError(error: unknown): void {
  toast.error(extractMutationErrorMessage(error));
}
